import React, { useState, useEffect } from 'react';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../types/enums';
import {
  CompanyArtifactId,
  PrimeArtifactId,
  CArtifactStateItem,
  CreateCArtifactStateItem,
} from '../../types/artifacts-branded';
import {
  StandardOperationalState,
  StandardImprovementState,
  getStateType,
  areStatesSameType
} from '../../types/standardStates';
import styles from './StateForm.module.css';

interface StateFormData {
  name: string;
  description: string;
  state_type: ArtifactStateTransitionType;
  is_required: boolean;
  is_initial: boolean;
  is_final: boolean;
  is_improvement: boolean;
  is_resource_active: boolean;
  demand_type: DemandType;
  demand_flow_type: DemandFlowType;
  state_metadata: Record<string, unknown>;
  state_rules: Record<string, unknown>;
  state_parameters: Record<string, unknown>;
  validation_rules: Record<string, unknown>;
}

interface StateFormProps {
  onSubmit: (state: CArtifactStateItem | CreateCArtifactStateItem) => void;
  onCancel: () => void;
  initialState?: CArtifactStateItem | CreateCArtifactStateItem; // Accept union type from UnifiedStateManager
  usePrime?: boolean;
  artifactId: CompanyArtifactId | PrimeArtifactId;
  inline?: boolean;
}

export const StateForm: React.FC<StateFormProps> = ({
  onSubmit,
  onCancel,
  initialState,
  usePrime = false,
  artifactId,
  inline = false,
}) => {
  const [formData, setFormData] = useState<StateFormData>({
    name: '',
    description: '',
    state_type: ArtifactStateTransitionType.OPERATIONAL,
    is_required: true,
    is_initial: false,
    is_final: false,
    is_improvement: false,
    is_resource_active: false,
    demand_type: DemandType.SUPPLY,
    demand_flow_type: DemandFlowType.PULL,
    state_metadata: {},
    state_rules: {},
    state_parameters: {},
    validation_rules: {},
    ...initialState,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (initialState) {
      setFormData((prev: StateFormData) => ({
        ...prev,
        ...initialState,
      }));
    }
  }, [initialState]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev: StateFormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.is_initial && formData.is_final) {
      newErrors.is_initial = 'A state cannot be both initial and final';
      newErrors.is_final = 'A state cannot be both initial and final';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      ...(usePrime && { prime_artifact_ref: artifactId }),
      ...(!usePrime && { artifact_ref: artifactId }),
    };

    onSubmit(submitData);
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        ...(usePrime && { prime_artifact_ref: artifactId }),
        ...(!usePrime && { artifact_ref: artifactId }),
      };
      onSubmit(submitData);
    }
  };

  const formContent = (
    <>
      <div className={styles.stateFormHeader}>
        <div className={styles.stateFormHeaderLeft}>
          <h3>{initialState ? 'Edit State' : 'Create New State'}</h3>
          <span className={styles.stateFormType}>{usePrime ? 'Prime State' : 'Regular State'}</span>
        </div>
        <div className={styles.stateFormHeaderActions}>
          <button type="button" onClick={handleCancel} className={styles.cancelButton}>
            Cancel
          </button>
          <button type="button" onClick={handleSaveClick} className={styles.submitButton}>
            Save
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.stateFormContent} style={{ display: 'none' }}>
        {/* Hidden form for submission */}
      </form>

      {/* Tab Navigation */}
      <div className={styles.stateFormTabs}>
        <button
          type="button"
          className={`${styles.stateFormTab} ${activeTab === 'basic' ? styles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          type="button"
          className={`${styles.stateFormTab} ${activeTab === 'properties' ? styles.active : ''}`}
          onClick={() => setActiveTab('properties')}
        >
          Properties
        </button>
        <button
          type="button"
          className={`${styles.stateFormTab} ${activeTab === 'state-rules' ? styles.active : ''}`}
          onClick={() => setActiveTab('state-rules')}
        >
          State Rules
        </button>
        <button
          type="button"
          className={`${styles.stateFormTab} ${activeTab === 'parameters' ? styles.active : ''}`}
          onClick={() => setActiveTab('parameters')}
        >
          Parameters
        </button>
        <button
          type="button"
          className={`${styles.stateFormTab} ${activeTab === 'validation' ? styles.active : ''}`}
          onClick={() => setActiveTab('validation')}
        >
          Validation Rules
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.stateFormTabContent}>
        {activeTab === 'basic' && (
          <div className={styles.stateFormTabPanel}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  id="standard_state_selector"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleInputChange({
                        target: { name: 'name', value: e.target.value }
                      } as React.ChangeEvent<HTMLInputElement>);
                      e.target.value = ''; // Reset selector
                    }
                  }}
                  style={{ flex: '0 0 auto', width: '200px' }}
                >
                  <option value="">Select Standard State...</option>
                  <optgroup label="Operational States">
                    {Object.values(StandardOperationalState).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Improvement States">
                    {Object.values(StandardImprovementState).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </optgroup>
                </select>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Or enter custom state name"
                  style={{ flex: '1' }}
                  required
                />
              </div>
              {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                rows={3}
                placeholder="Describe the state and its purpose..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="state_type">State Type *</label>
              <select
                id="state_type"
                name="state_type"
                value={formData.state_type}
                onChange={handleInputChange}
                required
              >
                <option value={ArtifactStateTransitionType.OPERATIONAL}>Operational</option>
                <option value={ArtifactStateTransitionType.MATERIAL}>Material</option>
                <option value={ArtifactStateTransitionType.RESOURCE}>Resource</option>
                <option value={ArtifactStateTransitionType.CREATION}>Creation</option>
                <option value={ArtifactStateTransitionType.IMPROVEMENT}>Improvement</option>
              </select>
            </div>

            <div className={styles.formSection}>
              <h4>State Properties</h4>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}></span>
                  Required State
                </label>
                {errors.is_required && <span className={styles.errorMessage}>{errors.is_required}</span>}
              </div>

              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_initial"
                    checked={formData.is_initial}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}></span>
                  Initial State
                </label>
                {errors.is_initial && <span className={styles.errorMessage}>{errors.is_initial}</span>}
              </div>

              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_final"
                    checked={formData.is_final}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}></span>
                  Final State
                </label>
                {errors.is_final && <span className={styles.errorMessage}>{errors.is_final}</span>}
              </div>

              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="is_improvement"
                    checked={formData.is_improvement}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}></span>
                  Improvement State
                </label>
              </div>
            </div>

            {usePrime && (
              <div className={styles.formSection}>
                <h4>Demand Configuration</h4>
                <div className="form-group">
                  <label htmlFor="demand_type">Demand Type</label>
                  <select
                    id="demand_type"
                    name="demand_type"
                    value={formData.demand_type}
                    onChange={handleInputChange}
                  >
                    <option value={DemandType.SUPPLY}>Supply (Default)</option>
                    <option value={DemandType.TARGET}>Target</option>
                    <option value={DemandType.CAPACITY}>Capacity</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="demand_flow_type">Demand Flow Type</label>
                  <select
                    id="demand_flow_type"
                    name="demand_flow_type"
                    value={formData.demand_flow_type}
                    onChange={handleInputChange}
                  >
                    <option value={DemandFlowType.PULL}>Pull (Default)</option>
                    <option value={DemandFlowType.DRUM}>Drum</option>
                    <option value={DemandFlowType.PUSH}>Push</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'properties' && (
          <div className={styles.stateFormTabPanel}>
            <div className="form-group">
              <label htmlFor="state_metadata">State Metadata (JSON)</label>
              <textarea
                id="state_metadata"
                name="state_metadata"
                value={JSON.stringify(formData.state_metadata, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData((prev: StateFormData) => ({ ...prev, state_metadata: parsed }));
                  } catch (error) {
                    // Allow invalid JSON during typing
                  }
                }}
                rows={12}
                placeholder='{"key": "value"}'
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className={styles.fieldHint}>Enter JSON object for state metadata</p>
            </div>
          </div>
        )}

        {activeTab === 'state-rules' && (
          <div className={styles.stateFormTabPanel}>
            <div className="form-group">
              <label htmlFor="state_rules">State Rules (JSON)</label>
              <textarea
                id="state_rules"
                name="state_rules"
                value={JSON.stringify(formData.state_rules, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData((prev: StateFormData) => ({ ...prev, state_rules: parsed }));
                  } catch (error) {
                    // Allow invalid JSON during typing
                  }
                }}
                rows={12}
                placeholder='{"rule": "value"}'
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className={styles.fieldHint}>Enter JSON object for state rules</p>
            </div>
          </div>
        )}

        {activeTab === 'parameters' && (
          <div className={styles.stateFormTabPanel}>
            <div className="form-group">
              <label htmlFor="state_parameters">State Parameters (JSON)</label>
              <textarea
                id="state_parameters"
                name="state_parameters"
                value={JSON.stringify(formData.state_parameters, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData((prev: StateFormData) => ({ ...prev, state_parameters: parsed }));
                  } catch (error) {
                    // Allow invalid JSON during typing
                  }
                }}
                rows={12}
                placeholder='{"parameter": "value"}'
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className={styles.fieldHint}>Enter JSON object for state parameters</p>
            </div>
          </div>
        )}

        {activeTab === 'validation' && (
          <div className={styles.stateFormTabPanel}>
            <div className="form-group">
              <label htmlFor="validation_rules">Validation Rules (JSON)</label>
              <textarea
                id="validation_rules"
                name="validation_rules"
                value={JSON.stringify(formData.validation_rules, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData((prev: StateFormData) => ({ ...prev, validation_rules: parsed }));
                  } catch (error) {
                    // Allow invalid JSON during typing
                  }
                }}
                rows={12}
                placeholder='{"validation": "rule"}'
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className={styles.fieldHint}>Enter JSON object for validation rules</p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div className={`${styles.stateForm} ${styles.stateFormInline}`}>
        {formContent}
      </div>
    );
  }

  return (
    <div className={styles.stateFormOverlay}>
      <div className={styles.stateForm}>
        {formContent}
      </div>
    </div>
  );
};
